import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { House, CurrencyDollar, Warning, CheckCircle, MagnifyingGlass, Clock } from "@phosphor-icons/react";

export default function TurnDashboard() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("open"); // open | all | completed

  useEffect(() => {
    api.get("/turns/dashboard").then(r => setData(r.data)).catch(() => setData({ turns: [], total: 0 }));
  }, []);

  if (!data) return <AdminShell><div className="p-8 font-bold">Loading turns…</div></AdminShell>;

  const turns = (data.turns || []).filter(t => {
    if (filter === "open" && t.status === "completed") return false;
    if (filter === "completed" && t.status !== "completed") return false;
    if (q) {
      const s = q.toLowerCase();
      return (t.property_address || "").toLowerCase().includes(s)
          || (t.customer_name || "").toLowerCase().includes(s)
          || (t.title || "").toLowerCase().includes(s)
          || (t.work_order_number || "").toLowerCase().includes(s);
    }
    return true;
  });

  const openCount = data.turns.filter(t => t.status !== "completed").length;
  const totalChargeback = data.turns.reduce((s, t) => s + (t.chargeback_total || 0), 0);
  const pendingReviews = data.turns.reduce((s, t) => s + (t.chargeback_pending || 0), 0);

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <House weight="bold" size={28} className="text-amber-600"/>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Turn Dashboard</h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 font-medium">Every open Turn at a glance — Move-Out & Move-In progress, tenant chargebacks, and totals.</p>

        {/* KPI row */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Open Turns" value={openCount} testid="kpi-open"/>
          <Kpi label="Total Turns" value={data.turns.length} testid="kpi-total"/>
          <Kpi label="Pending Chargeback Reviews" value={pendingReviews} accent={pendingReviews > 0 ? "text-amber-600" : "text-emerald-700"} testid="kpi-pending"/>
          <Kpi label="Chargeback $ Documented" value={`$${totalChargeback.toFixed(2)}`} accent="text-red-700" testid="kpi-money"/>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass weight="bold" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by property, tenant, WO#…"
              data-testid="turn-search-input"
              className="w-full h-11 pl-9 pr-3 border-2 border-[#0f172a] font-medium text-sm"/>
          </div>
          <div className="flex gap-1 border-2 border-[#0f172a]" role="tablist">
            {[["open", "Open"], ["completed", "Completed"], ["all", "All"]].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                data-testid={`turn-filter-${k}`}
                className={`h-11 px-3 font-black uppercase text-xs tracking-wider ${filter === k ? "bg-[#0f172a] text-white" : "bg-white"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Turn cards */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {turns.length === 0 && (
            <div className="col-span-full rugged-card p-8 text-center bg-white" data-testid="turn-empty">
              <House weight="bold" size={40} className="mx-auto text-slate-400"/>
              <div className="mt-2 font-black uppercase text-sm tracking-wider text-slate-500">
                {q ? "No matches" : "No turns yet"}
              </div>
              <p className="mt-1 text-xs text-slate-500">Create a Turn job from Admin → Jobs → New Job.</p>
            </div>
          )}
          {turns.map(t => (
            <TurnCard key={t.id} turn={t}/>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}

function Kpi({ label, value, testid, accent = "text-[#0f172a]" }) {
  return (
    <div className="rugged-card p-4 bg-white" data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display font-black text-2xl sm:text-3xl ${accent}`}>{value}</div>
    </div>
  );
}

function TurnCard({ turn }) {
  const moPct = turn.total_rooms ? (turn.move_out_completed / turn.total_rooms) * 100 : 0;
  const miPct = turn.total_rooms ? (turn.move_in_completed / turn.total_rooms) * 100 : 0;
  const isDone = turn.status === "completed";
  return (
    <Link to={`/admin/jobs/${turn.id}`} data-testid={`turn-card-${turn.id}`}
      className="rugged-card p-4 bg-white hover:shadow-[6px_6px_0px_#0f172a] transition-shadow block">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isDone ? (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white bg-emerald-600 inline-flex items-center gap-1">
                <CheckCircle weight="fill" size={10}/> Complete
              </span>
            ) : (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white bg-amber-500 inline-flex items-center gap-1">
                <Clock weight="fill" size={10}/> Open
              </span>
            )}
            {turn.work_order_number && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase font-mono bg-[#0f172a] text-white">
                {turn.work_order_number}
              </span>
            )}
            {turn.turn_flat_price > 0 && (
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                ${turn.turn_flat_price.toFixed(0)}
              </span>
            )}
          </div>
          <h3 className="mt-1 font-display font-black text-lg leading-tight truncate">{turn.property_address || "—"}</h3>
          <div className="text-xs text-slate-600 truncate">
            {turn.customer_name || "—"}{turn.assigned_to_name ? ` · ${turn.assigned_to_name}` : ""}
          </div>
        </div>
      </div>

      {/* Progress bars */}
      <div className="mt-3 space-y-2">
        <ProgressBar label="Move-Out" color="bg-red-600" pct={moPct} done={turn.move_out_completed} total={turn.total_rooms}/>
        <ProgressBar label="Move-In" color="bg-emerald-600" pct={miPct} done={turn.move_in_completed} total={turn.total_rooms}/>
      </div>

      {/* Chargeback stat */}
      {turn.chargeback_count > 0 && (
        <div className="mt-3 p-2 border-l-4 border-red-600 bg-red-50 flex items-center justify-between gap-2">
          <div className="text-xs font-black uppercase tracking-wider text-red-700 inline-flex items-center gap-1">
            <CurrencyDollar weight="bold" size={14}/> Chargeback
          </div>
          <div className="text-right">
            <div className="text-sm font-black text-red-700">${turn.chargeback_total.toFixed(2)}</div>
            <div className="text-[10px] font-bold text-slate-600">
              {turn.chargeback_count} flagged{turn.chargeback_pending > 0 ? ` · ${turn.chargeback_pending} pending review` : " · all reviewed"}
            </div>
          </div>
        </div>
      )}
    </Link>
  );
}

function ProgressBar({ label, color, pct, done, total }) {
  const isComplete = total > 0 && done === total;
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-slate-600">
        <span>{label}</span>
        <span className={isComplete ? "text-emerald-700" : ""}>{done}/{total || 0} rooms</span>
      </div>
      <div className="mt-1 h-2 bg-slate-200 relative overflow-hidden border border-slate-300">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}
